import { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast as sonnerToast } from "sonner";
import type { UploadedImage } from "@/components/image-upload";
import AbandonedCartBanner from "@/components/AbandonedCartBanner";
import type { Tables } from "@/integrations/supabase/types";
import {
  FormData,
  initialFormData,
  PlanType,
  essentialsSteps,
  FormStep,
} from "./types";
import {
  OccasionStep,
  VideoDateStep,
  RecipientStep,
  NameCountStep,
  NamesStep,
  PronunciationStep,
  EmailStep,
  FeelingStep,
  StyleStep,
  TempoStep,
  LanguageStep,
  StoryStep,
  PhotosStep,
  ReviewStep,
  AccountStep,
} from "./steps";

interface SongOrderFormProps {
  initialPlan?: PlanType;
}

const SongOrderForm = ({ initialPlan = "essentials" }: SongOrderFormProps) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const planFromUrl = searchParams.get("plan") as PlanType | null;
  const stepFromUrl = searchParams.get("step");
  
  const [formData, setFormData] = useState<FormData>(() => {
    // Try to restore form data from sessionStorage
    const saved = localStorage.getItem("songFormData");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return { ...initialFormData, ...parsed, planType: planFromUrl || parsed.planType || initialPlan };
      } catch {}
    }
    return {
      ...initialFormData,
      planType: planFromUrl || initialPlan,
    };
  });
  
  const [currentStepIndex, setCurrentStepIndex] = useState(() => {
    if (stepFromUrl) return parseInt(stepFromUrl, 10) || 0;
    return 0;
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [surveySessionId, setSurveySessionId] = useState<string | null>(null);
  const [showAccountStep, setShowAccountStep] = useState(false);
  const [unpaidOrders, setUnpaidOrders] = useState<Tables<"song_orders">[]>([]);
  const [dismissedUnpaid, setDismissedUnpaid] = useState<Set<string>>(new Set());

  // Check for unpaid orders when user is authenticated
  useEffect(() => {
    if (!user) return;
    const fetchUnpaid = async () => {
      const { data } = await supabase
        .from("song_orders")
        .select("*")
        .is("price_paid", null)
        .eq("status", "pending_payment" as any)
        .order("created_at", { ascending: false });
      if (data) setUnpaidOrders(data);
    };
    fetchUnpaid();
  }, [user]);

  // Dynamic step order for Premium: photos comes right after videoDate if "now" is selected
  const steps = useMemo((): readonly FormStep[] => {
    if (formData.planType === "essentials") {
      return essentialsSteps;
    }
    
    // Premium flow: if uploadPhotosNow is "now", photos comes right after videoDate
    if (formData.uploadPhotosNow === "now") {
      return [
        "occasion",
        "recipient",
        "nameCount",
        "names",
        "pronunciation",
        "feeling",
        "style",
        "tempo",
        "language",
        "videoDate",
        "photos",
        "story",
        "review",
      ] as const;
    }
    
    // Default premium flow (photos at end or skipped if "later")
    return [
      "occasion",
      "recipient",
      "nameCount",
      "names",
      "pronunciation",
      "feeling",
      "style",
      "tempo",
      "language",
      "videoDate",
      "story",
      "review",
    ] as const;
  }, [formData.planType, formData.uploadPhotosNow]);

  const currentStep = steps[currentStepIndex];
  const totalSteps = steps.length;

  // Persist form data to sessionStorage (excluding non-serializable fields)
  useEffect(() => {
    const { pronunciationRecording, photos, ...serializableData } = formData;
    localStorage.setItem("songFormData", JSON.stringify(serializableData));
  }, [formData]);

  // Update URL with current step index
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("step", String(currentStepIndex));
    setSearchParams(newParams, { replace: true });
  }, [currentStepIndex]);

  // Force auth gate if user landed past the videoDate step without being logged in
  useEffect(() => {
    if (!user && !showAccountStep) {
      const videoDateIndex = steps.indexOf("videoDate");
      if (videoDateIndex >= 0 && currentStepIndex >= videoDateIndex) {
        setShowAccountStep(true);
      }
    }
  }, [user, currentStepIndex, steps, showAccountStep]);

  useEffect(() => {
    if (!user) return;
    
    const createSession = async () => {
      const { data, error } = await supabase
        .from("abandoned_surveys")
        .insert({
          user_id: user.id,
          plan_type: formData.planType,
          current_step: "occasion",
          step_index: 0,
        } as any)
        .select("id")
        .single();
      
      if (!error && data) {
        setSurveySessionId(data.id);
      }
    };
    
    createSession();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update survey progress when step changes
  useEffect(() => {
    if (!surveySessionId || !user) return;
    
    supabase
      .from("abandoned_surveys")
      .update({
        current_step: currentStep,
        step_index: currentStepIndex,
        occasion: formData.occasion || null,
        recipient: formData.recipient || null,
        names: formData.names || null,
      } as any)
      .eq("id", surveySessionId)
      .then(({ error }) => {
        if (error) console.error("Survey tracking update error:", error);
      });
  }, [currentStepIndex, currentStep]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleRecordingChange = (blob: Blob | null, url: string) => {
    setFormData((prev) => ({
      ...prev,
      pronunciationRecording: blob,
      pronunciationRecordingUrl: url,
    }));
  };

  const handlePhotosChange = (photos: File[], urls: string[]) => {
    setFormData((prev) => ({
      ...prev,
      photos,
      photoUrls: urls,
    }));
  };

  const handleUploadedImagesChange = (images: UploadedImage[]) => {
    setFormData((prev) => ({
      ...prev,
      uploadedImages: images,
    }));
  };

  const handleRushChange = (value: boolean) => {
    setFormData((prev) => ({ ...prev, rushDelivery: value }));
  };

  const canProceed = useCallback((): boolean => {
    switch (currentStep) {
      case "occasion":
        return formData.occasion !== "" && 
          (formData.occasion !== "other" || formData.occasionOther !== "");
      case "videoDate":
        return formData.uploadPhotosNow !== "";
      case "recipient":
        return formData.recipient !== "" &&
          (formData.recipient !== "other" || formData.recipientOther !== "");
      case "nameCount":
        return formData.nameCount !== "";
      case "names":
        return formData.names.trim() !== "";
      case "pronunciation":
        return formData.pronunciationRecordingUrl !== "";
      case "feeling": {
        const feelings = formData.feeling ? formData.feeling.split(",") : [];
        return feelings.length > 0 &&
          (!feelings.includes("other") || formData.feelingOther !== "");
      }
      case "style":
        return formData.songStyle !== "" &&
          (formData.songStyle !== "other" || formData.songStyleOther !== "");
      case "tempo":
        return formData.tempo !== "" &&
          (formData.tempo !== "other" || formData.tempoOther !== "");
      case "language":
        return formData.language !== "" && formData.singerGender !== "";
      case "story":
        return formData.story.trim().length >= 50;
      case "photos":
        return formData.uploadPhotosNow === "later" || formData.uploadedImages.length >= 10;
      case "review":
        return true;
      default:
        return false;
    }
  }, [currentStep, formData]);

  const handleNext = useCallback(() => {
    if (currentStepIndex < totalSteps - 1) {
      const nextStep = steps[currentStepIndex + 1];
      // Premium flow: require auth before videoDate step (same as essentials checkout)
      if (nextStep === "videoDate" && !user) {
        setShowAccountStep(true);
        return;
      }
      setCurrentStepIndex((prev) => prev + 1);
    }
  }, [currentStepIndex, totalSteps, steps, user]);

  const handleBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex((prev) => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (!user) {
      // Show inline account creation step instead of error toast
      setShowAccountStep(true);
      return;
    }
    await executeSubmit();
  };

  const handleAuthenticated = () => {
    setShowAccountStep(false);
    // If we're at the review step, auto-submit; otherwise advance to next step
    if (currentStep === "review") {
      setTimeout(() => executeSubmit(), 800);
    } else {
      // Advance to the next step (e.g., videoDate after language)
      setTimeout(() => setCurrentStepIndex((prev) => prev + 1), 500);
    }
  };

  const executeSubmit = async () => {
    const currentUser = user;
    // Re-check auth - user might have just signed up
    const { data: { user: freshUser } } = await supabase.auth.getUser();
    const submitUser = freshUser || currentUser;
    if (!submitUser) {
      toast({ title: "Please log in", description: "You must be logged in to submit an order.", variant: "destructive" });
      return;
    }
    setIsSubmitting(true);
    
    try {
      // Check if customer is blocked
      const { data: blockCheck } = await supabase.rpc("is_customer_blocked", { _email: submitUser.email || "" });
      if (blockCheck === true) {
        toast({ title: "Account Restricted", description: "Your account has been restricted. Please contact support for assistance.", variant: "destructive" });
        setIsSubmitting(false);
        return;
      }
      // Map form data to database columns
      const occasionValue = formData.occasion === "other" ? formData.occasionOther : formData.occasion;
      const feelingValue = formData.feeling === "other" ? formData.feelingOther : formData.feeling;
      const styleValue = formData.songStyle === "other" ? formData.songStyleOther : formData.songStyle;
      const tempoValue = formData.tempo === "other" ? formData.tempoOther : formData.tempo;

      // Upload pronunciation recording to storage if present
      let pronunciationUrl: string | null = null;
      if (formData.pronunciationRecording) {
        const fileName = `${submitUser.id}/${formData.uploadSessionId}/pronunciation.webm`;
        const { error: uploadError } = await supabase.storage
          .from("customer-photos")
          .upload(fileName, formData.pronunciationRecording, {
            contentType: "audio/webm",
            upsert: true,
          });
        if (uploadError) {
          console.error("Pronunciation upload error:", uploadError);
        } else {
          pronunciationUrl = fileName;
        }
      }

      const SPANISH_DOMAINS = ["tucancionmomento.com", "www.tucancionmomento.com"];
      const sourceLang = SPANISH_DOMAINS.includes(window.location.hostname) ? "es" : "en";

      const { data: order, error } = await supabase.from("song_orders").insert({
        user_id: submitUser.id,
        event_type: occasionValue as any,
        recipient_names: formData.names,
        relationship_to_recipient: formData.recipient === "other" ? formData.recipientOther : formData.recipient,
        mood: feelingValue,
        musical_style: styleValue as any,
        tempo: tempoValue,
        memories: formData.story,
        plan_type: formData.planType,
        
        language: formData.language,
        singer_gender: formData.singerGender || null,
        rush_delivery: formData.rushDelivery,
        pronunciation_url: pronunciationUrl,
        status: "pending_payment" as any,
        source_lang: sourceLang,
      } as any).select("id").single();

      if (error) throw error;

      // Link uploaded images to the order — use upload_session_id to catch ALL images
      // (even if formData.uploadedImages was lost from sessionStorage)
      if (formData.uploadSessionId) {
        await supabase
          .from("image_uploads")
          .update({ order_id: order.id })
          .eq("upload_session_id", formData.uploadSessionId)
          .is("order_id", null);
      }

      // Mark abandoned survey as completed
      if (surveySessionId) {
        supabase
          .from("abandoned_surveys")
          .update({ completed: true } as any)
          .eq("id", surveySessionId)
          .then(({ error: surveyErr }) => {
            if (surveyErr) console.error("Survey completion update error:", surveyErr);
          });
      }

      // Now redirect to Stripe checkout (notification will be sent after payment verification)
      const { data: checkoutData, error: checkoutError } = await supabase.functions.invoke("create-checkout", {
        body: {
          planType: formData.planType,
          rushDelivery: formData.rushDelivery,
          orderId: order.id,
          successUrl: `${window.location.origin}/checkout/success?order_id=${order.id}&session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/create?plan=${formData.planType}`,
        },
      });

      if (checkoutError) throw checkoutError;
      
      if (checkoutData?.url) {
        // Clean up form data from sessionStorage on successful submission
        localStorage.removeItem("songFormData");
        window.location.href = checkoutData.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (error: any) {
      console.error("Submit error:", error);
      sonnerToast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    if (showAccountStep) {
      return <AccountStep onAuthenticated={handleAuthenticated} />;
    }
    switch (currentStep) {
      case "occasion":
        return <OccasionStep formData={formData} onChange={handleInputChange} />;
      case "videoDate":
        return <VideoDateStep formData={formData} onChange={handleInputChange} />;
      case "recipient":
        return <RecipientStep formData={formData} onChange={handleInputChange} />;
      case "nameCount":
        return <NameCountStep formData={formData} onChange={handleInputChange} />;
      case "names":
        return <NamesStep formData={formData} onChange={handleInputChange} />;
      case "pronunciation":
        return <PronunciationStep formData={formData} onRecordingChange={handleRecordingChange} />;
      case "feeling":
        return <FeelingStep formData={formData} onChange={handleInputChange} />;
      case "style":
        return <StyleStep formData={formData} onChange={handleInputChange} />;
      case "tempo":
        return <TempoStep formData={formData} onChange={handleInputChange} />;
      case "language":
        return <LanguageStep formData={formData} onChange={handleInputChange} />;
      case "story":
        return <StoryStep formData={formData} onChange={handleInputChange} />;
      case "photos":
        return <PhotosStep formData={formData} onPhotosChange={handlePhotosChange} onUploadedImagesChange={handleUploadedImagesChange} />;
      case "review":
        return <ReviewStep formData={formData} onRushChange={handleRushChange} onRecordingChange={handleRecordingChange} />;
      default:
        return null;
    }
  };

  // Enter key advances the form
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      // Don't intercept Enter in textareas (for multi-line input)
      const target = e.target as HTMLElement;
      if (target.tagName === "TEXTAREA") return;

      e.preventDefault();
      if (currentStep === "review") {
        if (!isSubmitting) handleSubmit();
      } else if (canProceed()) {
        handleNext();
      }
    }
  }, [currentStep, isSubmitting, canProceed, handleNext, handleSubmit]);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div className="w-full">
      {/* Abandoned Cart Banner */}
      {unpaidOrders.filter(o => !dismissedUnpaid.has(o.id)).length > 0 && currentStepIndex === 0 && (
        <div className="space-y-3 mb-6">
          <AnimatePresence>
            {unpaidOrders.filter(o => !dismissedUnpaid.has(o.id)).map((o) => (
              <AbandonedCartBanner
                key={o.id}
                order={o}
                onDismiss={(id) => setDismissedUnpaid(prev => new Set(prev).add(id))}
              />
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-muted-foreground">
            {showAccountStep ? "Create Account" : `Step ${currentStepIndex + 1} of ${totalSteps}`}
          </span>
          <span className="text-sm text-muted-foreground">
            {showAccountStep ? "Almost there!" : `${Math.round(((currentStepIndex + 1) / totalSteps) * 100)}% Complete`}
          </span>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-primary"
            initial={{ width: 0 }}
            animate={{ width: `${((currentStepIndex + 1) / totalSteps) * 100}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
      </div>

      {/* Plan Badge */}
      <div className="text-center mb-4">
        <span className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium ${
          formData.planType === "premium" 
            ? "bg-primary/10 text-primary" 
            : "bg-muted text-muted-foreground"
        }`}>
          {formData.planType === "premium" ? "Premium Song + Slideshow" : "Essentials Plan"}
        </span>
      </div>

      {/* Form Content */}
      <motion.div
        className="bg-card rounded-2xl shadow-elevated p-6 md:p-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <AnimatePresence mode="wait">
          {renderStep()}
        </AnimatePresence>

        {/* Navigation */}
        {showAccountStep ? (
          <div className="flex justify-start mt-8 pt-6 border-t border-border">
            <Button
              variant="ghost"
              onClick={() => setShowAccountStep(false)}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Review
            </Button>
          </div>
        ) : (
          <div className="flex justify-between mt-8 pt-6 border-t border-border">
            <Button
              variant="ghost"
              onClick={handleBack}
              disabled={currentStepIndex === 0}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>

            {currentStep === "review" ? (
              <Button
                variant="hero"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Proceed to Payment
                  </>
                )}
              </Button>
            ) : (
              <Button
                onClick={handleNext}
                disabled={!canProceed()}
              >
                {currentStep === "story" ? "Continue to Review" : "Continue"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default SongOrderForm;
